<template>
  <div class="client-transit approved-list">
    <div class="client-heading">
      <div>
        <p v-if="isAccounting" class="client-eyebrow">
          Portal del distribuidor
        </p>
        <h1>{{ isAccounting ? 'Contabilidad' : stageTitle }}</h1>
        <p v-if="!isAccounting" class="approved-subtitle">
          {{ stageDescription }} Los trámites se agrupan en consolidados (CONS-)
          e individuales (IND-).
        </p>
      </div>
    </div>
    <p v-if="stage === 'approved'" class="payment-legend">
      <span
        ><i class="payment-dot payment-dot--red" />Rojo: sin pago ni monto</span
      >
      <span
        ><i class="payment-dot payment-dot--orange" />Naranja: completar monto y
        comprobante</span
      >
      <span
        ><i class="payment-dot payment-dot--green" />Verde: pagado · ver
        comprobante</span
      >
    </p>
    <v-card class="client-card">
      <nav
        v-if="!isAccounting"
        class="client-tabs"
        aria-label="Etapas del trámite"
      >
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :class="{ active: stage === tab.value }"
          :aria-current="stage === tab.value ? 'page' : null"
          @click="changeStage(tab.value)"
        >
          {{ tab.text }}
        </button>
      </nav>
      <form class="approved-filters" @submit.prevent="onSearch">
        <div class="approved-filter approved-filter--search">
          <label for="approved-search">Buscar</label>
          <v-text-field
            id="approved-search"
            v-model="search"
            placeholder="# factura, consecutivo, control, bulto, CIF o laboratorio…"
            append-icon="search"
            clearable
            solo
            flat
            hide-details
          />
        </div>
        <div class="approved-filter">
          <label for="approved-distributor">Distribuidor</label>
          <v-select
            id="approved-distributor"
            v-model="filters.distId"
            :items="filterOptions.distributors"
            item-text="name"
            item-value="id"
            placeholder="Todos"
            clearable
            solo
            flat
            hide-details
            @change="filters.labId = ''"
          />
        </div>
        <div class="approved-filter">
          <label for="approved-lab">Laboratorio</label>
          <v-select
            id="approved-lab"
            v-model="filters.labId"
            :items="availableLaboratories"
            item-text="name"
            item-value="id"
            placeholder="Todos"
            clearable
            solo
            flat
            hide-details
          />
        </div>
        <div v-if="stage === 'approved'" class="approved-filter">
          <label for="approved-payment">Pago de impuestos</label>
          <v-select
            id="approved-payment"
            v-model="filters.paymentStatus"
            :items="paymentOptions"
            solo
            flat
            hide-details
            @change="onSearch"
          />
        </div>
        <div class="approved-filter">
          <label for="approved-from">Recepción desde</label>
          <input
            id="approved-from"
            v-model="filters.startDate"
            type="date"
            :max="filters.endDate || undefined"
          />
        </div>
        <div class="approved-filter">
          <label for="approved-to">Recepción hasta</label>
          <input
            id="approved-to"
            v-model="filters.endDate"
            type="date"
            :min="filters.startDate || undefined"
          />
        </div>
        <div class="approved-filter-actions">
          <span v-if="filterError" role="alert">{{ filterError }}</span>
          <button
            type="button"
            class="tx-btn tx-btn--ghost"
            @click="clearFilters"
          >
            Limpiar
          </button>
          <button
            type="submit"
            class="tx-btn tx-btn--primary"
            :disabled="loading"
          >
            <v-icon small>search</v-icon> Buscar
          </button>
          <button
            type="button"
            class="tx-btn tx-btn--primary"
            :disabled="exporting || loading"
            @click="download"
          >
            <v-icon small>cloud_download</v-icon>
            {{ exporting ? 'Exportando…' : 'Exportar' }}
          </button>
        </div>
        <p v-if="filterOptionsError" class="approved-filter-error" role="alert">
          {{ filterOptionsError }}
          <button type="button" @click="loadFilterOptions">Reintentar</button>
        </p>
      </form>
      <div class="tx-toolbar">
        <span class="tx-chip"
          ><v-icon small class="tx-chip__icon">receipt</v-icon>
          {{ total }} trámite{{ total === 1 ? '' : 's'
          }}<template v-if="totalInvoices !== total">
            ({{ totalInvoices }} facturas)</template
          ></span
        >
      </div>
      <v-progress-linear v-if="loading" indeterminate height="3" class="ma-0" />
      <div v-if="error" class="client-error" role="alert">
        {{ error }} <button @click="load">Reintentar</button>
      </div>
      <div class="client-table-wrap">
        <table class="client-table tx-table">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                :class="columnClass(column.key)"
              >
                {{ column.text }}
              </th>
            </tr>
          </thead>
          <tbody v-for="group in groups" :key="group.key" class="tx-group">
            <tr
              class="tx-group-row"
              :class="{ 'tx-group-row--single': !esConsolidado(group) }"
            >
              <td :colspan="columns.length">
                <div class="approved-band">
                  <button
                    type="button"
                    class="approved-band-label"
                    :aria-expanded="!collapsed[group.key]"
                    @click="toggleGroup(group.key)"
                  >
                    <v-icon small class="tx-group-row__icon">{{
                      esConsolidado(group) ? 'layers' : 'description'
                    }}</v-icon>
                    {{ etiquetaTramite(group) }}
                    <strong>{{ consecutivoDe(group) }}</strong> ·
                    {{ facturasDe(group) }}
                    <span
                      class="approved-pill"
                      :class="'approved-pill--' + stage"
                      >{{ statusLabel }}</span
                    >
                    <span
                      v-if="esConsolidado(group)"
                      class="approved-band-totals"
                    >
                      · CIF total USD {{ decimal(group.totals.cif) }} ·
                      {{ decimal(group.totals.grossWeight) }} kg ·
                      {{ group.totals.bultosSum }} bultos</span
                    >
                    <span
                      v-if="controlFdDe(group)"
                      class="approved-band-totals"
                    >
                      · Control F&amp;D: {{ controlFdDe(group) }}</span
                    >
                    <v-icon small>{{
                      collapsed[group.key] ? 'expand_more' : 'expand_less'
                    }}</v-icon>
                  </button>
                  <tax-payment-button
                    v-if="
                      !isAccounting &&
                        !isRejectedStage &&
                        (stage !== 'approved' || taxInHeader(group))
                    "
                    :disabled="!canOpenTax(group)"
                    :group="group"
                    :amount="money(group.tramite.taxAmount)"
                    @click="openTax(group, group.invoices[0])"
                  />
                </div>
              </td>
            </tr>
            <tr
              v-for="(row, index) in group.invoices"
              :key="row.id"
              v-show="!collapsed[group.key]"
              :class="{
                'tx-child-row': esConsolidado(group),
              }"
            >
              <template v-for="column in columns">
                <td
                  v-if="
                    (isAccounting ||
                      column.key !== 'tax' ||
                      (!taxInHeader(group) && celdaTramite(group, index))) &&
                      (!isRejectedStage ||
                        column.key !== 'details' ||
                        celdaTramite(group, index))
                  "
                  :key="column.key"
                  :colspan="
                    taxInHeader(group) && column.key === 'comments' ? 2 : 1
                  "
                  :rowspan="
                    !isAccounting &&
                    (column.key === 'tax' ||
                      (isRejectedStage && column.key === 'details'))
                      ? filasTramite(group)
                      : 1
                  "
                  :class="[
                    columnClass(column.key),
                    {
                      'th-center':
                        !isAccounting &&
                        [
                          'tatApproval',
                          'details',
                          'tax',
                          'packageNumber',
                        ].includes(column.key),
                      'tx-cell--tramite':
                        !isAccounting &&
                        column.key === 'tax' &&
                        esConsolidado(group),
                      'tx-cell--strong tx-cell--factura':
                        !isAccounting && column.key === 'laboratorio',
                    },
                  ]"
                >
                  <button
                    v-if="column.key === 'details'"
                    :class="
                      !isAccounting ? 'client-detail-icon' : 'client-link'
                    "
                    title="Ver detalles"
                    aria-label="Ver detalles"
                    @click="details(row, group)"
                  >
                    <v-icon v-if="!isAccounting" small>visibility</v-icon>
                    <template v-else
                      >Ver detalles</template
                    >
                  </button>
                  <tax-payment-button
                    v-else-if="column.key === 'tax'"
                    :disabled="!canOpenTax(group)"
                    :group="group"
                    :amount="money(group.tramite.taxAmount)"
                    @click="openTax(group, row)"
                  />
                  <button
                    v-else-if="
                      column.key === 'reimbursement' || column.key === 'service'
                    "
                    class="client-status"
                    :title="
                      column.key === 'service'
                        ? 'Ver y descargar factura por servicios'
                        : 'Ver y descargar factura por reembolso'
                    "
                    :class="issued(group, column.key) ? 'green' : 'red'"
                    @click="openBilling(group, row, column.key)"
                  >
                    <v-icon aria-hidden="true">{{
                      issued(group, column.key) ? 'check_circle' : 'schedule'
                    }}</v-icon>
                    {{ issued(group, column.key) ? 'EMITIDA' : 'PENDIENTE'
                    }}<span v-if="issued(group, column.key)">{{
                      money(
                        column.key === 'service'
                          ? group.serviceTotal
                          : group.reimbursementTotal
                      )
                    }}</span>
                  </button>
                  <button
                    v-else-if="column.key === 'statement'"
                    class="client-status"
                    title="Ver y descargar estado de cuenta"
                    :class="group.statementIssued ? 'green' : 'red'"
                    @click="openStatements(group)"
                  >
                    <v-icon aria-hidden="true">{{
                      group.statementIssued ? 'check_circle' : 'schedule'
                    }}</v-icon>
                    {{ group.statementIssued ? 'EMITIDO' : 'PENDIENTE' }}
                  </button>
                  <button
                    v-else-if="column.key === 'status'"
                    class="client-status"
                    :class="{
                      red: stage === 'denied',
                      orange: stage === 'reentered',
                      muted: stage === 'pending' && row.checkCase !== 'YES',
                      green:
                        !isRejectedStage &&
                        !(stage === 'pending' && row.checkCase !== 'YES'),
                    }"
                    :title="
                      isRejectedStage
                        ? 'Ver comentarios y documentos de rechazo'
                        : 'Ver detalles del trámite'
                    "
                    @click="isRejectedStage ? rejection(row) : details(row)"
                  >
                    <v-icon aria-hidden="true">{{
                      isRejectedStage
                        ? 'cancel'
                        : stage === 'pending' && row.checkCase !== 'YES'
                        ? 'schedule'
                        : 'check_circle'
                    }}</v-icon>
                    {{
                      stage === 'pending' && row.checkCase === 'YES'
                        ? 'EN PROCESO'
                        : statusLabel
                    }}
                  </button>
                  <span
                    v-else-if="column.key === 'notary'"
                    :title="
                      row.notary ? 'Requiere notaría' : 'No requiere notaría'
                    "
                    >{{ row.notary ? '✅' : '❌' }}</span
                  >
                  <processing-comments
                    v-else-if="column.key === 'comments'"
                    :row="row"
                  />
                  <span v-else-if="column.key === 'requirements'">{{
                    missingRequirements(row).join(' · ') || 'Listo para iniciar'
                  }}</span>
                  <span v-else>{{ cell(row, column.key) }}</span>
                </td>
              </template>
            </tr>
            <tr
              v-if="isRejectedStage"
              v-show="!collapsed[group.key]"
              class="client-rejection-row"
            >
              <td :colspan="columns.length">
                <div class="client-rejection">
                  <div class="client-rejection-reason">
                    <strong>Motivo del rechazo</strong>
                    <p>
                      {{ rejectionReason(group) || 'Sin motivo registrado.' }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="tx-btn tx-btn--ghost"
                    @click="rejectionDocuments(group)"
                  >
                    <v-icon small>attach_file</v-icon> Comprobantes del rechazo
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-if="!groups.length">
            <tr>
              <td :colspan="columns.length" class="client-empty">
                {{
                  loading
                    ? 'Cargando trámites…'
                    : error
                    ? 'No se pudo cargar el listado.'
                    : appliedFilters.paymentStatus === 'incomplete'
                    ? 'No hay pagos con monto registrado pendientes de completar.'
                    : 'No hay trámites en esta etapa con los filtros aplicados.'
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="pages > 1" class="client-pagination">
        <v-pagination
          v-model="page"
          :length="pages"
          :total-visible="7"
          @input="load"
        />
      </div>
    </v-card>
    <v-dialog v-model="documentDialog.show" max-width="650">
      <v-card
        ><v-card-title
          ><h3>{{ documentDialog.title }}</h3>
          <v-spacer /><v-btn icon @click="documentDialog.show = false"
            ><v-icon>close</v-icon></v-btn
          ></v-card-title
        >
        <v-card-text>
          <p v-if="documentDialog.comment" class="client-comment">
            {{ documentDialog.comment }}
          </p>
          <p v-if="documentDialog.error" class="client-error" role="alert">
            {{ documentDialog.error }}
          </p>
          <p v-if="documentDialog.loading">Cargando documentos…</p>
          <p v-else-if="!documentDialog.error && !documentDialog.files.length">
            {{
              documentDialog.title === 'Comprobante de impuestos'
                ? 'El pago está registrado, pero no tiene un comprobante adjunto.'
                : documentDialog.title === 'Rechazo del trámite'
                ? 'No hay documentos de rechazo adjuntos.'
                : 'No hay documentos adjuntos.'
            }}
          </p>
          <div
            v-for="(file, index) in documentDialog.files"
            :key="file.filePath || index"
            class="client-file"
          >
            <div>
              <span>{{ file.originalName || 'Documento adjunto' }}</span>
              <p
                v-if="file.comment && file.comment !== documentDialog.comment"
                class="client-comment"
              >
                {{ file.comment }}
              </p>
            </div>
            <a
              v-if="file.available !== false"
              :href="fileUrl(file.filePath)"
              target="_blank"
              rel="noopener"
              >Ver / descargar</a
            >
            <span v-else>Archivo no disponible</span>
          </div>
          <router-link
            v-if="documentDialog.statements"
            to="/contabilidad/estado-de-cuenta"
            >Ver estados de cuenta</router-link
          >
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="payment.show"
      max-width="520"
      :persistent="payment.saving"
    >
      <v-card
        ><v-card-title><h3>Pago de impuestos del cliente</h3></v-card-title
        ><v-card-text>
          <p>{{ payment.reference }}</p>
          <v-text-field
            v-model="payment.amount"
            label="Monto pagado (B/.)"
            type="number"
            min="0.01"
            step="0.01"
            :disabled="payment.saving"
          />
          <label class="client-upload"
            >Comprobante (máximo 20 MB)<input
              ref="receipt"
              type="file"
              :disabled="payment.saving"
              @change="selectFile"
          /></label>
          <p v-if="payment.error" class="client-error" role="alert">
            {{ payment.error }}
          </p> </v-card-text
        ><v-card-actions
          ><v-spacer /><v-btn
            flat
            :disabled="payment.saving"
            @click="payment.show = false"
            >Cancelar</v-btn
          ><v-btn color="primary" :loading="payment.saving" @click="savePayment"
            >Guardar pago</v-btn
          ></v-card-actions
        ></v-card
      >
    </v-dialog>
    <v-dialog
      v-if="detail.data || detail.items.length"
      v-model="detail.show"
      max-width="1024"
      ><compose
        :item="detail.data"
        :items="detail.items"
        :code="detail.code"
        :show-reentry-history="stage === 'reentered'"
        @close-detail="detail.show = false"
    /></v-dialog>
    <v-snackbar v-model="notice.show" :color="notice.color"
      >{{ notice.text
      }}<v-btn flat @click="notice.show = false">Cerrar</v-btn></v-snackbar
    >
  </div>
</template>

<script>
import ClientTransit from '@/api/ClientTransit'
import TramiteBand from './mixins/TramiteBand'
import TaxPaymentButton from '@/components/TaxPaymentButton'
import ProcessingComments from '@/components/ProcessingComments'
import compose from './../dialog/DialogDetails'
import { dateFormat, getFullPath } from '@/helpers/TransitHelper'

const column = (key, text) => ({ key, text })
const base = [
  column('invoiceNumber', '# Factura'),
  column('laboratorio', 'Laboratorio'),
]
const received = [
  column('receivedDate', 'Fecha envío'),
  column('organo', 'Órgano anuente'),
  column('notary', 'Notaría'),
]
export default {
  mixins: [TramiteBand],
  components: { compose, ProcessingComments, TaxPaymentButton },
  data() {
    return {
      tabs: [
        { value: 'pending', text: 'Trámites para Procesar' },
        { value: 'process', text: 'Trámites en Proceso' },
        { value: 'approved', text: 'Aprobaciones' },
        { value: 'denied', text: 'Rechazos' },
        { value: 'reentered', text: 'Trámites para Reingreso' },
      ],
      search: '',
      term: '',
      groups: [],
      page: 1,
      total: 0,
      totalInvoices: 0,
      collapsed: {},
      filters: {
        distId: '',
        labId: '',
        startDate: '',
        endDate: '',
        paymentStatus: '',
      },
      paymentOptions: [
        { text: 'Todos', value: '' },
        { text: 'Pagados', value: 'paid' },
        { text: 'Pendientes', value: 'pending' },
        { text: 'Naranjas', value: 'incomplete' },
      ],
      appliedFilters: {},
      filterOptions: { distributors: [], laboratories: [] },
      filterOptionsError: '',
      filterError: '',
      rpp: 25,
      loading: false,
      exporting: false,
      error: '',
      request: 0,
      documentDialog: {
        show: false,
        title: '',
        comment: '',
        files: [],
        loading: false,
        statements: false,
      },
      payment: {
        show: false,
        saving: false,
        amount: '',
        file: null,
        id: null,
        reference: '',
        error: '',
      },
      detail: { show: false, data: null, items: [], code: '' },
      notice: { show: false, text: '', color: 'success' },
    }
  },
  computed: {
    stageTitle() {
      return (
        (this.tabs.find(tab => tab.value === this.stage) || {}).text ||
        'Gestión Aduanera'
      )
    },
    stageDescription() {
      return (
        {
          pending:
            'Consulte los trámites pendientes de predeclaración y documentación.',
          process:
            'El TAT de aprobación cuenta días hábiles desde el ingreso al órgano anuente hasta su resolución; excluye sábados, domingos y los feriados y días festivos registrados.',
          approved: 'Consulte sus facturas aprobadas y los pagos de impuestos.',
          denied: 'Consulte los trámites rechazados y los motivos de rechazo.',
          reentered:
            'Consulte los trámites rechazados pendientes de reingreso. Al reingresarlos, vuelven a Trámites para Procesar.',
        }[this.stage] || ''
      )
    },
    availableLaboratories() {
      return this.filterOptions.laboratories.filter(
        lab =>
          !this.filters.distId ||
          lab.distributorIds.includes(this.filters.distId)
      )
    },
    isAccounting() {
      return this.$route.path === '/contabilidad/cliente'
    },
    isRejectedStage() {
      return ['denied', 'reentered'].includes(this.stage)
    },
    stage() {
      if (this.isAccounting) return 'accounting'
      const value = this.$route.query.etapa
      return this.tabs.some(tab => tab.value === value) ? value : 'pending'
    },
    pages() {
      return Math.ceil(this.total / this.rpp)
    },
    statusLabel() {
      return {
        pending: 'PENDIENTE',
        process: 'EN PROCESO',
        approved: 'APROBADO',
        denied: 'RECHAZADO',
        reentered: 'PARA REINGRESO',
        accounting: 'APROBADO',
      }[this.stage]
    },
    columns() {
      if (this.stage === 'pending')
        return [
          ...base,
          ...received,
          column('status', 'Estado'),
          column('comments', 'Comentarios'),
        ]
      if (this.stage === 'process')
        return [
          ...base,
          ...received,
          column('admissionDate', 'Fecha de inicio'),
          column('fdcontrolNumber', 'Control de trámite'),
          column('details', 'Detalles del trámite'),
          column('comments', 'Comentarios'),
        ]
      if (this.stage === 'approved')
        return [
          column('invoiceNumber', 'Factura'),
          column('laboratorio', 'Laboratorio'),
          column('admissionDate', 'Fecha de inicio'),
          column('fdcontrolNumber', 'Control de trámite'),
          column('departureDate', 'Fecha de aprobación'),
          column('details', 'Detalle del trámite'),
          column('status', 'Estado del trámite'),
          column('comments', 'Comentarios'),
          column('tax', 'Pago de impuestos'),
        ]
      if (this.isRejectedStage)
        return [
          column('invoiceNumber', 'Facturas'),
          column('laboratorio', 'Laboratorio'),
          column('admissionDate', 'Fecha de inicio'),
          column('fdcontrolNumber', 'Control del trámite'),
          column('deniedDate', 'Fecha de rechazo'),
          column('details', 'Detalle del trámite'),
          column('status', 'Estado del trámite'),
          column('comments', 'Comentarios'),
        ]
      return [
        ...base,
        column('status', 'Estado del trámite'),
        column('tax', 'Pago de impuestos'),
        column('reimbursement', 'Facturación por reembolso'),
        column('service', 'Facturación por servicios'),
        column('statement', 'Estado de cuenta total'),
      ]
    },
  },
  watch: {
    stage() {
      this.page = 1
      this.appliedFilters = {}
      this.filters = {
        distId: '',
        labId: '',
        startDate: '',
        endDate: '',
        paymentStatus: '',
      }
      this.search = ''
      this.term = ''
      this.filterError = ''
      this.collapsed = {}
      this.load()
      this.loadFilterOptions()
    },
  },
  created() {
    this.load()
    this.loadFilterOptions()
  },
  methods: {
    taxInHeader(group) {
      return this.stage === 'approved' && this.esConsolidado(group)
    },
    columnClass(key) {
      if (this.isAccounting)
        return [
          'status',
          'tax',
          'reimbursement',
          'service',
          'statement',
        ].includes(key)
          ? 'th-center'
          : ''
      if (['cif', 'grossWeight'].includes(key)) return 'th-right tx-nowrap'
      if (
        [
          'packageNumber',
          'tatApproval',
          'total_fda',
          'details',
          'tax',
        ].includes(key)
      )
        return 'th-center'
      return ''
    },
    decimal(value) {
      return value == null
        ? '—'
        : Number(value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
    },
    toggleGroup(key) {
      this.$set(this.collapsed, key, !this.collapsed[key])
    },
    async loadFilterOptions() {
      this.filterOptionsError = ''
      try {
        this.filterOptions = await ClientTransit.filters()
      } catch (error) {
        this.filterOptionsError =
          'No se pudieron cargar los filtros de distribuidor y laboratorio.'
      }
    },
    clearFilters() {
      this.search = ''
      this.filters = {
        distId: '',
        labId: '',
        startDate: '',
        endDate: '',
        paymentStatus: '',
      }
      this.onSearch()
    },
    missingRequirements(row) {
      return [
        !row.predeclarationReady && 'Predeclaración terminada',
        (!row.documentsComplete || row.pendingDocument) &&
          'Documentos completos',
      ].filter(Boolean)
    },
    money(value) {
      return (
        'B/. ' +
        Number(value || 0).toLocaleString('es-PA', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      )
    },
    cell(row, key) {
      if (
        [
          'createdAt',
          'receivedDate',
          'admissionDate',
          'departureDate',
          'deniedDate',
          'previousAdmissionDate',
          'reinitDate',
        ].includes(key)
      )
        return row[key] ? dateFormat(row[key], 'DD/MM/YYYY') : '—'
      if (['cif', 'grossWeight'].includes(key)) return this.decimal(row[key])
      if (key === 'packageNumber') return row[key] == null ? '—' : row[key]
      if (key === 'organo')
        return (
          (row.organosAnuentes || []).map(item => item.name).join(', ') || '—'
        )
      if (key === 'comments')
        return (
          [
            ...new Set([row.processComment, row.observations].filter(Boolean)),
          ].join('\n') || '—'
        )
      if (key === 'tatApproval' && row[key] == null)
        return 'Pendiente de ingreso'
      if (key === 'total_fda')
        return row[key] == null || row[key] < 0
          ? '—'
          : `${row[key]} día(s) hábil(es)`
      if (key.startsWith('tat'))
        return row[key] == null ? '—' : `${row[key]} día(s) hábil(es)`
      return row[key] || '—'
    },
    fileUrl(path) {
      return getFullPath(path)
    },
    issued(group, kind) {
      return !!group.tramite[
        kind === 'service' ? 'serviceInvoiceDate' : 'reimbursementInvoiceDate'
      ]
    },
    taxLabel(group) {
      return group.tramite.taxPaidDate
        ? 'PAGADO'
        : Number(group.tramite.taxAmount || 0) > 0
        ? 'COMPLETAR PAGO'
        : 'SIN PAGO NI MONTO'
    },
    changeStage(value) {
      this.$router.push({
        path: '/transit/ver-facturas',
        query: { etapa: value },
      })
    },
    onSearch() {
      this.filterError = ''
      {
        const { startDate, endDate } = this.filters
        if (
          (startDate && !endDate) ||
          (!startDate && endDate) ||
          (startDate && startDate > endDate)
        ) {
          this.filterError =
            'Seleccione ambas fechas, con la fecha inicial anterior o igual a la final.'
          return
        }
        this.appliedFilters = { ...this.filters }
      }
      this.collapsed = {}
      this.term = this.search || ''
      this.page = 1
      this.load()
    },
    async load() {
      const request = ++this.request
      this.loading = true
      this.error = ''
      this.groups = []
      try {
        const result = await ClientTransit.list({
          ...this.appliedFilters,
          stage: this.stage,
          term: this.term,
          rpp: this.rpp,
          page: this.page,
        })
        if (request !== this.request) return
        this.groups = result.groups
        this.total = result.totalGroups
        this.totalInvoices = result.totalInvoices
      } catch (error) {
        if (request === this.request) {
          this.error = 'No se pudieron cargar los trámites.'
          this.total = 0
          this.totalInvoices = 0
        }
      } finally {
        if (request === this.request) this.loading = false
      }
    },
    async details(row, group) {
      try {
        const isGroup =
          this.isRejectedStage && group && this.esConsolidado(group)
        const rows = isGroup ? group.invoices : [row]
        const invoices = await Promise.all(
          rows.map(async invoice => ({
            ...invoice,
            lab_invoices: { name: invoice.laboratorio },
            dist_invoices: { name: invoice.distribuidor },
            products: await ClientTransit.products(invoice.id),
          }))
        )
        this.detail = {
          show: true,
          data: isGroup ? null : invoices[0],
          items: isGroup ? invoices : [],
          code: isGroup ? this.consecutivoDe(group) : '',
        }
      } catch (error) {
        this.notify('No se pudieron cargar los detalles.', 'error')
      }
    },
    async documents(row, step, title, comment = '') {
      const dialog = {
        show: true,
        title,
        comment,
        error: '',
        files: [],
        loading: true,
        statements: false,
      }
      this.documentDialog = dialog
      try {
        const rows = Array.isArray(row) ? row : [row]
        const responses = await Promise.all(
          rows.map(invoice => ClientTransit.documents(invoice.id, step))
        )
        const files = new Map()
        responses.forEach(documents =>
          documents.forEach(file => files.set(file.filePath, file))
        )
        dialog.files = [...files.values()]
      } catch (error) {
        dialog.error =
          'No se pudieron cargar los documentos. Vuelve a intentarlo.'
      } finally {
        dialog.loading = false
      }
    },
    rejection(row) {
      this.documents(
        row,
        'DENIED',
        'Rechazo del trámite',
        row.observationsDenied || ''
      )
    },
    rejectionReason(group) {
      return [
        ...new Set(
          group.invoices
            .map(row => (row.observationsDenied || '').trim())
            .filter(Boolean)
        ),
      ].join(' / ')
    },
    rejectionDocuments(group) {
      this.documents(
        group.invoices,
        'DENIED',
        'Rechazo del trámite',
        this.rejectionReason(group)
      )
    },
    canOpenTax(group) {
      return (
        !!group.tramite.taxPaidDate ||
        (group.invoices.every(row => row.fdApproved === 'YES') &&
          group.clientCanPay !== false)
      )
    },
    openTax(group, row) {
      if (!this.canOpenTax(group)) return
      if (group.tramite.taxPaidDate) {
        this.documents(row, 'PAYED', 'Comprobante de impuestos')
        return
      }
      if (group.clientCanPay === false) {
        this.notify(
          'El consolidado no admite un pago desde esta cuenta.',
          'error'
        )
        return
      }
      this.payment = {
        show: true,
        saving: false,
        amount:
          group.tramite.taxAmount == null
            ? ''
            : String(group.tramite.taxAmount),
        file: null,
        id: row.id,
        reference: group.tramite.internalNumber || row.invoiceNumber,
        error: '',
      }
      this.$nextTick(() => {
        if (this.$refs.receipt) this.$refs.receipt.value = ''
      })
    },
    openBilling(group, row, kind) {
      this.documents(
        row,
        kind === 'service' ? 'SERVICE' : 'REIMBURSEMENT',
        kind === 'service'
          ? 'Facturación por servicios'
          : 'Facturación por reembolso'
      )
    },
    openStatements(group) {
      const files = []
      const seen = new Set()
      ;(group.statements || []).forEach(statement =>
        (statement.documents || []).forEach(file => {
          if (!seen.has(file.filePath)) {
            seen.add(file.filePath)
            files.push(file)
          }
        })
      )
      this.documentDialog = {
        show: true,
        title: 'Estado de cuenta',
        comment: (group.statements || []).map(s => s.number).join(' · '),
        files,
        loading: false,
        statements: true,
      }
    },
    selectFile(event) {
      this.payment.file = event.target.files[0] || null
    },
    async savePayment() {
      if (this.payment.saving) return
      if (
        !/^\d{1,11}(\.\d{1,2})?$/.test(String(this.payment.amount)) ||
        Number(this.payment.amount) <= 0
      ) {
        this.payment.error = 'Ingrese un monto válido mayor que cero.'
        return
      }
      if (
        !this.payment.file ||
        this.payment.file.size > 20 * 1024 * 1024 ||
        !this.payment.file.size
      ) {
        this.payment.error = 'Adjunte un comprobante de hasta 20 MB.'
        return
      }
      this.payment.saving = true
      this.payment.error = ''
      try {
        await ClientTransit.pay(
          this.payment.id,
          this.payment.amount,
          this.payment.file
        )
        this.payment.show = false
        this.notify('Pago y comprobante guardados.')
        await this.load()
      } catch (error) {
        this.payment.error =
          'No se pudo guardar el pago. Verifica que el trámite siga pendiente y pertenezca a tu cuenta.'
      } finally {
        this.payment.saving = false
      }
    },
    notify(text, color = 'success') {
      this.notice = { show: true, text, color }
    },
    async download() {
      this.exporting = true
      try {
        const result = await ClientTransit.list({
          ...this.appliedFilters,
          stage: this.stage,
          term: this.term,
          rpp: '',
        })
        const quote = value =>
          '"' +
          String(value == null ? '' : value)
            .replace(/^[=+@-]/, "'$&")
            .replace(/"/g, '""') +
          '"'
        const exportColumns = this.isRejectedStage
          ? [
              ...this.columns,
              column('observationsDenied', 'Motivo del rechazo'),
            ]
          : this.columns
        const rows = [exportColumns.map(c => c.text)]
        result.groups.forEach(group =>
          group.invoices.forEach(row =>
            rows.push(
              exportColumns.map(c => {
                if (c.key === 'status') return this.statusLabel
                if (c.key === 'notary') return row.notary ? 'Sí' : 'No'
                if (c.key === 'tax')
                  return `${this.taxLabel(group)} ${this.money(
                    group.tramite.taxAmount
                  )}`
                if (c.key === 'details') return ''
                if (c.key === 'statement')
                  return (
                    (group.statements || []).map(s => s.number).join(', ') ||
                    'PENDIENTE'
                  )
                if (['service', 'reimbursement'].includes(c.key))
                  return this.issued(group, c.key)
                    ? this.money(
                        c.key === 'service'
                          ? group.serviceTotal
                          : group.reimbursementTotal
                      )
                    : 'PENDIENTE'
                return this.cell(row, c.key)
              })
            )
          )
        )
        const url = URL.createObjectURL(
          new Blob(
            ['\ufeff' + rows.map(row => row.map(quote).join(',')).join('\r\n')],
            { type: 'text/csv;charset=utf-8;' }
          )
        )
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.stage}.csv`
        link.click()
        URL.revokeObjectURL(url)
      } catch (error) {
        this.notify('No se pudo descargar el listado.', 'error')
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped src="./transit-list.css"></style>

<style scoped>
.client-transit .client-status {
  min-height: 44px;
  padding: 8px 12px;
}
.client-transit .client-status span {
  white-space: nowrap;
}

.payment-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin: 0 0 16px;
  color: #475569;
  font-size: 12px;
}
.payment-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.payment-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.payment-dot--red {
  background: #b91c1c;
}
.payment-dot--orange {
  background: #c2410c;
}
.payment-dot--green {
  background: #15803d;
}

.client-transit {
  padding: 24px;
  color: #172133;
}
.client-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.client-heading h1 {
  font-size: 26px;
  margin: 0;
}
.client-eyebrow {
  color: #64748b;
  margin-bottom: 4px;
}
.client-card {
  border-radius: 12px;
  overflow: hidden;
}
.client-tabs {
  display: flex;
  overflow-x: auto;
  border-bottom: 1px solid #e2e8f0;
}
.client-tabs button {
  white-space: nowrap;
  padding: 18px;
  font-weight: 600;
  border-bottom: 3px solid transparent;
}
.client-tabs button.active {
  color: #254f87;
  border-color: #254f87;
  background: #f0f5fb;
}
.client-search {
  display: flex;
  gap: 18px;
  align-items: center;
  padding: 16px 20px;
}
.client-search .v-input {
  max-width: 440px;
}
.client-table-wrap {
  overflow-x: auto;
}
.client-rejection-row td {
  background: #fafbfc;
}
.client-rejection {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
}
.client-rejection-reason {
  flex: 1 1 260px;
  min-width: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.client-rejection-reason strong {
  color: #991b1b;
}
.client-rejection-reason p {
  margin: 4px 0 0;
}
.client-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.client-table th {
  background: #f8fafc;
  color: #475569;
  text-align: left;
  padding: 10px 12px;
  white-space: nowrap;
}
.client-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0;
  min-width: 100px;
}
.client-table td:last-child {
  max-width: 300px;
  white-space: pre-wrap;
}
.client-group td {
  background: #edf3fa;
  color: #254f87;
  font-weight: 600;
}
.client-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 5px;
  padding: 5px 8px;
  min-height: 28px;
  font-size: 11px;
  line-height: 18px;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
}
.client-status .v-icon {
  font-size: 15px;
  color: inherit;
}
.client-status span {
  padding-left: 5px;
  border-left: 1px solid currentColor;
  font-size: 11px;
}
.client-status:hover {
  filter: brightness(0.94);
}
.green {
  background: #15803d;
  color: white;
}
.red {
  background: #dc2626;
  color: white;
}
.orange {
  background: #fbbf24;
  color: #422006;
}
.muted {
  background: #e2e8f0;
  color: #475569;
}
.client-detail-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #eff6ff;
}
.client-detail-icon .v-icon {
  color: #254f87;
}
.client-table.tx-table td.th-center,
.client-table.tx-table th.th-center {
  text-align: center;
}
.client-link {
  color: #254f87;
  text-decoration: underline;
}
.client-empty {
  text-align: center;
  padding: 40px !important;
  color: #64748b;
}
.client-pagination {
  padding: 18px;
  display: flex;
  justify-content: center;
}
.client-error {
  color: #b91c1c;
  padding: 12px;
}
.client-error button {
  text-decoration: underline;
}
.client-file {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 0;
  border-bottom: 1px solid #e2e8f0;
}
.client-comment {
  white-space: pre-wrap;
}
.client-upload input {
  display: block;
  margin-top: 12px;
  max-width: 100%;
}
button:focus-visible,
a:focus-visible {
  outline: 2px solid #254f87;
  outline-offset: 3px;
}
@media (max-width: 600px) {
  .client-transit {
    padding: 12px;
  }
  .client-heading,
  .client-search {
    flex-wrap: wrap;
  }
  .client-heading h1 {
    font-size: 22px;
  }
}
</style>

<style scoped>
.approved-subtitle {
  color: #475569;
  margin: 8px 0 0;
  max-width: 70ch;
}
.approved-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}
.approved-filter {
  flex: 1 1 150px;
  min-width: 0;
}
.approved-filter--search {
  flex: 2 1 260px;
}
.approved-filter label {
  display: block;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.approved-filter >>> .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  box-shadow: none !important;
}
.approved-filter input[type='date'] {
  width: 100%;
  min-height: 48px;
  padding: 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #f8fafc;
}
.approved-filter-actions {
  flex: 1 0 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.approved-filter-actions span,
.approved-filter-error {
  color: #b91c1c;
}
.approved-filter-error button {
  text-decoration: underline;
}
.approved-band {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.approved-band > .tax-button {
  position: sticky;
  right: 0;
  z-index: 1;
  flex: 0 0 auto;
  margin-left: auto;
}
.approved-band-label {
  white-space: normal;
  flex: 1 1 360px;
  min-width: 0;
  text-align: left;
  line-height: 1.8;
}
.approved-band-label > .v-icon:last-child {
  float: right;
  margin: 4px;
}
.approved-band-totals {
  color: #475569;
}
.approved-pill {
  display: inline-block;
  color: #166534;
  background: #dcfce7;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  margin: 0 6px;
}
.approved-list .client-table.tx-table .th-right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.approved-list .client-table.tx-table .tx-group-row td {
  white-space: normal;
  padding: 10px 16px;
}
.approved-pill--pending {
  background: #f1f5f9;
  color: #475569;
}
.approved-pill--process {
  background: #dbeafe;
  color: #1e40af;
}
.approved-pill--denied {
  background: #fee2e2;
  color: #991b1b;
}
.approved-pill--reentered {
  background: #fef3c7;
  color: #854d0e;
}
.approved-list .client-table.tx-table .tx-cell--strong {
  font-weight: 600;
}
.approved-list .client-table.tx-table td {
  white-space: normal;
  vertical-align: middle;
}
.approved-list .client-table.tx-table td.tx-cell--strong {
  min-width: 180px;
}
.approved-list .client-table.tx-table .tx-nowrap {
  white-space: nowrap;
}
.approved-list .client-table-wrap {
  width: 100%;
}
.approved-list .client-heading > div {
  min-width: 0;
  width: 100%;
}
.approved-filter-actions .v-icon {
  color: inherit;
}
.approved-list .client-table.tx-table th {
  padding: 14px 12px;
}
.approved-filter input:focus-visible {
  outline: 2px solid #254f87;
  outline-offset: 2px;
}
@media (max-width: 600px) {
  .approved-filter {
    flex-basis: 100%;
  }
  .approved-filters {
    padding: 14px;
  }
  .approved-filter-actions {
    justify-content: flex-start;
  }
}
</style>
